/* SolNow — store de la ticketera nativa (Pinia via storeFactory).
   El "estado" de un ticket es su columna (ticket_column_id); las columnas son
   dinámicas (ver stores/ticketColumns.js). Los eliminados van a un "cementerio"
   (deletedRecords): salen del tablero pero se ven en la lista y se restauran. */
import TicketsAPI from 'dashboard/api/tickets';
import { createStore } from 'dashboard/store/storeFactory';

export const useTicketsStore = createStore({
  name: 'tickets',
  type: 'pinia',
  API: TicketsAPI,

  state: () => ({
    deletedRecords: [],
  }),

  getters: {
    getTicketsList: state => state.records,
    getTicketsByColumn: state => columnId =>
      state.records.filter(ticket => ticket.ticket_column_id === columnId),
    getDeletedList: state => state.deletedRecords,
    // Busca en activos Y en el cementerio, así el modal de detalle también
    // resuelve un ticket eliminado (para poder restaurarlo desde ahí).
    getRecord: state => id =>
      [...state.records, ...state.deletedRecords].find(
        record => record.id === Number(id)
      ) || {},
  },

  actions: () => ({
    // Insert-or-update. Enruta el ticket según deleted_at, de modo que el push
    // de ActionCable de una eliminación/restauración mueva el registro entre
    // activos y cementerio en vivo, sin refetch.
    upsertRecord(record) {
      if (record.deleted_at) {
        this.records = this.records.filter(r => r.id !== record.id);
        const di = this.deletedRecords.findIndex(r => r.id === record.id);
        if (di === -1) this.deletedRecords.push(record);
        else this.deletedRecords[di] = { ...this.deletedRecords[di], ...record };
        return;
      }
      this.deletedRecords = this.deletedRecords.filter(r => r.id !== record.id);
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = { ...this.records[index], ...record };
    },

    // Override del create del factory (que hace push ciego): usa upsert para
    // que la respuesta del POST y el push de ActionCable no dupliquen el ticket.
    async create(payload) {
      this.setUIFlag({ creatingItem: true });
      try {
        const { data } = await TicketsAPI.create(payload);
        const record = data.payload || data;
        this.upsertRecord(record);
        return record;
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    removeRecord(id) {
      this.records = this.records.filter(r => r.id !== id);
    },

    // Trae el cementerio (tickets eliminados) para la vista "Eliminados".
    async fetchDeleted() {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await TicketsAPI.getDeleted();
        this.deletedRecords = data.payload || data;
        return this.deletedRecords;
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    // Manda un ticket al cementerio (soft delete): sale del tablero al instante.
    async softDelete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        const { data } = await TicketsAPI.delete(id);
        const record = data.payload || data;
        if (record && record.id) this.upsertRecord(record);
        else this.removeRecord(id);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    // Restaura un ticket del cementerio al tablero.
    async restore(id) {
      const { data } = await TicketsAPI.restore(id);
      const record = data.payload || data;
      this.upsertRecord(record);
      return record;
    },

    // Mueve un ticket a otra columna del tablero: cambio optimista + persist.
    async moveToColumn({ id, ticketColumnId }) {
      const current = this.getRecord(id);
      this.upsertRecord({ ...current, id, ticket_column_id: ticketColumnId });
      // El controller espera params.require(:ticket), de ahí el wrap.
      await this.update({ id, ticket: { ticket_column_id: ticketColumnId } });
    },
  }),
});
